import Tooltip from "@/tooltip";
import { PhotoIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon } from "@/icons/solid";
import {
	addDays,
	addYears,
	differenceInCalendarDays,
	differenceInMinutes,
	format,
	getYear,
	isSameDay,
	isValid,
	parse,
	set,
	setHours,
} from "date-fns";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-notifications/lib/notifications.css";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Actions as EMPLOYEEACTIONS } from "../../store/actions/employeeActions";
import { Actions as ACTIONS } from "../../store/actions/leaveActions";
import { DateInputFormat, TimeInputFormat } from "../common/DateComponents";
import Spinner from "../common/Spinner";
import { toTitleCase, validateFloat } from "../common/validation/Validators";
import BenefitType from "./leave-breakdown/BenefitType";
import LeaveBreakdown from "./leave-breakdown/LeaveBreakdown";
import { CustomOption } from "@/components/common/custom-selectors/CustomOption";
import { LoadingDots } from "@/components/common/LoadingDots";
import { employmentTypeOptions } from "../company-settings/config/employmentTypeOptions";
import Modal from "@/common/Modal";

const today = set(new Date(), { minutes: 0, seconds: 0, milliseconds: 0 });

const formatTime = ({ time, debug }) => {
	if (!time) return "";
	const splitTime = time?.replaceAll(/\s/g, "").split(":");
	const newStartTime = `${splitTime[0]}:${splitTime[1]}`;
	if (debug) console.debug(time, " ==> ", newStartTime);
	return newStartTime;
};

const getTimeDifference = (dateLeft, dateRight) => {
	return parseFloat((differenceInMinutes(dateLeft, dateRight) / 60).toFixed(2));
};

const useOptionHandler = (data, options, key, updateField) => {
	const option = useMemo(() => {
		return options?.find((item) => item.value == data?.[key]);
	}, [JSON.stringify(options), data?.[key]]);
	const update = (value) => updateField(key, value);

	return [option, update];
};

const useDateHandler = (data, key, updateField) => {
	const extractedKey = useMemo(() => {
		return (data?.[key] && parse(data[key], "yyyy-MM-dd", today)) || today;
	}, [data?.[key]]);

	const date = set(extractedKey, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
	const update = (date) => updateField(key, format(date, "yyyy-MM-dd"));

	return [date, update];
};

const useTimeHandler = (data, key, updateField) => {
	const extractedKey = useMemo(() => {
		return (data?.[key] && parse(data[key], "HH:mm", today)) || today;
	}, [data?.[key]]);

	const time = set(extractedKey, { seconds: 0, milliseconds: 0 });
	const update = (time) => updateField(key, format(time, "HH:mm"));

	return [time, update];
};

const LeaveModal = ({
	modalState,
	modalSaving,
	modalLeave,
	setOpenModal,
	saveLeave,
	lockDate,
	leaveTypeOptions,
	currentAgreement,
	leaveStatusOptions,
	getLeave,
	clashingLeave,
	uploadFile,
	uploading,
	uploaded,
	employeeOptions,
	getLeaveBreakdown,
	jurisdiction,
	updateField,
	modalStatus,
	modalDetailsLoading,
	modalUpdateDates,
	modalUpdateDetails,
	displayEmployeeOptions,
	employee,
	location,
}) => {
	const { newEntry, isParentEntry, type, status, agreement, date, dateTo, hours, comment, weeklyBreakdown } =
		modalLeave;
	const { hoursAvailable, halfPayBalance, terminationDate, hoursPending, engagementDate } = currentAgreement;
	const { lastPaidDate } = modalStatus;

	const paidDate = lastPaidDate && parse(lastPaidDate, "yyyy-MM-dd", today);

	const useEmployeeHandler = (data, options, key, updateField) => {
		const option = useMemo(() => {
			return options?.find((item) => item.value == data?.[key]);
		}, [JSON.stringify(options), data?.[key]]);
		const update = (value) => updateField(key, value);
		if (location == "employeeLeave") return [employee, update];

		return [option, update];
	};

	const [selectedEmployee, setEmployee] = useEmployeeHandler(
		modalLeave,
		employeeOptions,
		"agreement",
		modalUpdateDetails,
	);

	const [selectedLeaveType, setLeaveType] = useOptionHandler(
		modalLeave,
		leaveTypeOptions,
		"type",
		modalUpdateDetails,
	);
	const [selectedStatus, setLeaveStatus] = useOptionHandler(modalLeave, leaveStatusOptions, "status", updateField);
	const [fromDate, setFromDate] = useDateHandler(modalLeave, "date", modalUpdateDates);
	const [timeIn, setTimeIn] = useTimeHandler(modalLeave, "startTime", modalUpdateDates);
	const [toDate, setToDate] = useDateHandler(modalLeave, "dateTo", modalUpdateDates);
	const [timeOut, setTimeOut] = useTimeHandler(modalLeave, "endTime", modalUpdateDates);
	const [notificationDate, setNotificationDate] = useDateHandler(modalLeave, "dateNotified", modalUpdateDates);

	const endOfYear = set(timeOut, { month: 11, date: 31 });

	const isPaid = date != dateTo ? !!lastPaidDate : modalStatus?.isPaid || modalLeave?.isPaid;

	const setHoursWorked = (hours) => updateField("hours", hours);
	const setComment = (comment) => updateField("comment", comment);
	const isMultiDay = date !== dateTo;
	const [warningContent, setWarningContent] = useState(false);

	useEffect(() => {
		if (isParentEntry) getLeaveBreakdown(modalLeave.id);
		if (!newEntry) return;
		modalUpdateDetails({
			agreement: (location == "employeeLeave" && employee?.value) || employeeOptions?.[0]?.value,
			type: 6020,
			status: leaveStatusOptions?.[0]?.value,
			date: format(today, "yyyy-MM-dd"),
			dateTo: format(today, "yyyy-MM-dd"),
		});
	}, [modalState]);

	useEffect(() => {
		if (fromDate && toDate && modalState)
			getLeave({
				from: date,
				to: dateTo,
				status: ["Approved", "Pending"], // "Approved"//
			});
	}, [modalState, date, dateTo]);

	// useEffect(() => {
	// 	if (isParentEntry) updateField("isParentEntry", false);
	// }, [type]);

	const clashingLeaveExcludingThis = clashingLeave?.filter((item) => item.id != modalLeave.id) || [];
	const alreadyHasLeave =
		agreement && clashingLeaveExcludingThis?.find((clashLeave) => clashLeave.agreement == agreement);
	const hasEnoughHours = selectedLeaveType?.applyIfBalanceAvailable ? hours <= hoursAvailable : true;
	const doesNotConsumeBalance = !["Cancelled", "Denied", "Deleted"].includes(status);
	const hasRequired = agreement && hours && fromDate && timeIn && timeOut;

	const SubmitButtons = () => {
		let TagName = "button";
		const props = {};
		let children;

		if (weeklyBreakdown) {
			TagName = Link;
			props.to = `/organisation/people/${agreement}#Children`;
			props.onClick = () => setOpenModal(false, null);
			props.className = "btn btn-primary pull-right";
			children = "View In Children";
		}

		if (modalSaving) {
			props.disabled = true;
			props.className = "btn btn-success pull-right";
			children = (
				<>
					<Spinner
						style={{ height: "1.5em", verticalAlign: "middle", float: "left" }}
						className="animate-spin -ml-1 mr-3 h-5 w-5"
					/>
					<div style={{ float: "left" }}> Saving </div>
				</>
			);
		} else if (!hasRequired) {
			props.disabled = true;
			props.className = "btn btn-default pull-right";
			children = "Leave Application Incomplete";
		} else if (alreadyHasLeave && doesNotConsumeBalance) {
			props.disabled = true;
			props.className = "btn btn-danger pull-right";
			children = `${selectedEmployee.label} already has a leave application`;
		} else if (!newEntry && !hasEnoughHours && doesNotConsumeBalance) {
			props.className = "btn btn-warning pull-right";
			children = "Insufficient Leave Available: Update Anyway";
			props.onClick = () => saveLeaveData();
		} else if (!hasEnoughHours && type != 6022 && doesNotConsumeBalance) {
			props.disabled = true;
			props.className = "btn btn-danger pull-right";
			children = "Insufficient Leave Available";
		} else if (type == 6022 && !uploaded?.length && doesNotConsumeBalance) {
			props.className = "btn btn-warning pull-right";
			children = "Save without Attachment";
			props.onClick = () => saveLeaveData();
		} else if (!newEntry) {
			props.className = "btn btn-success pull-right";
			children = "Update";
			props.onClick = () => saveLeaveData();
		} else {
			props.className = "btn btn-success pull-right";
			children = "Save";
			props.onClick = () => saveLeaveData();
		}

		if (modalDetailsLoading) {
			props.disabled = true;
		}

		return (
			<TagName
				{...props}
				style={{ marginRight: 0 }}
			>
				{children}
				{modalDetailsLoading && <LoadingDots />}
			</TagName>
		);
	};

	const saveLeaveData = () => {
		const dayDuration = differenceInCalendarDays(toDate, fromDate);

		if (dayDuration > 5 && !warningContent) {
			setWarningContent({
				title: "Warning: Leave Exceeds 5 days",
				message: `Kindly note that this leave is for ${hours} hours over ${dayDuration} days.`,
			});
			return;
		} else {
			if (warningContent) {
				setWarningContent(false);
			}
		}

		saveLeave();
	};

	// console.debug(weeklyBreakdown)

	if (!modalState) return null;
	return (
		<>
			<Modal
				status={modalState}
				close={() => setOpenModal(false, null)}
				title={`${!newEntry ? "Edit" : "Add"} ${isPaid ? "Paid" : ""} Leave`}
				width="50vw"
			>
				<div
					className="block-content max-h-[calc(95vh-111px-60px)] overflow-y-auto"
					style={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr 1fr",
						gap: "1rem",
					}}
				>
					<div
						style={{
							gridColumn: "1/3",
						}}
					>
						<div className="form-group">
							<div className="col-xs-6 pl-0">
								<label htmlFor="material-text">Employee</label>
								<Select
									value={selectedEmployee}
									options={displayEmployeeOptions}
									components={{ Option: CustomOption }}
									onChange={(selected) => setEmployee(selected.value)}
									isLoading={modalDetailsLoading}
									isDisabled={
										isPaid ||
										weeklyBreakdown ||
										!newEntry ||
										modalDetailsLoading ||
										location == "employeeLeave"
									}
								/>
							</div>

							<div className="col-xs-6 pl-0 pr-0">
								<label htmlFor="material-text">Type</label>
								<Select
									value={selectedLeaveType}
									options={leaveTypeOptions?.filter((item) => !item.parental)}
									onChange={(selected) => setLeaveType(selected.value)}
									isLoading={modalDetailsLoading}
									isDisabled={isPaid || weeklyBreakdown || modalDetailsLoading}
								/>
							</div>
							<div className="clearfix" />
						</div>

						<div className="row">
							<div className="col-xs-6 pr-0">
								<label>From</label>
								<DatePicker
									selected={isValid(fromDate) && fromDate}
									wrapperClassName="full-width"
									onChange={(newFromDate) => {
										if (!newFromDate) return;
										if (jurisdiction == "UK" && notificationDate < newFromDate) {
											setNotificationDate(newFromDate);
										}
										setFromDate(newFromDate);
									}}
									minDate={lockDate || (engagementDate && new Date(engagementDate))}
									maxDate={(terminationDate && new Date(terminationDate)) || addYears(new Date(), 1)}
									dateFormat="dd/MM/yyyy"
									customInput={<DateInputFormat />}
									disabled={isPaid || weeklyBreakdown}
								/>
							</div>

							<div className="col-xs-6 pl-0">
								<label htmlFor=" ">&nbsp;</label>
								<DatePicker
									wrapperClassName="full-width"
									selected={isValid(timeIn) && timeIn}
									onChange={(newTimeIn) => {
										setTimeIn(newTimeIn);
									}}
									showTimeSelect
									showTimeSelectOnly
									timeIntervals={15}
									timeCaption="Time"
									dateFormat="h:mm aa"
									customInput={<TimeInputFormat />}
									disabled={isPaid || weeklyBreakdown}
								/>
							</div>

							<div className="clearfix" />

							<div className="col-xs-6 pr-0">
								<label>To</label>
								<DatePicker
									selected={isValid(toDate) && toDate}
									wrapperClassName="full-width"
									onChange={(newToDate) => {
										if (!newToDate) return;
										setToDate(newToDate);
									}}
									minDate={isPaid ? paidDate : fromDate}
									maxDate={(terminationDate && new Date(terminationDate)) || endOfYear}
									disabled={paidDate >= toDate || weeklyBreakdown}
									dateFormat="dd/MM/yyyy"
									customInput={<DateInputFormat />}
								/>
							</div>

							<div className="col-xs-6 pl-0">
								<label htmlFor="">&nbsp;</label>
								<DatePicker
									wrapperClassName="full-width"
									selected={isValid(timeOut) && timeOut}
									onChange={(newTimeOut) => {
										setTimeOut(newTimeOut);
									}}
									showTimeSelect
									showTimeSelectOnly
									timeIntervals={15}
									timeCaption="Time"
									dateFormat="h:mm aa"
									customInput={<TimeInputFormat />}
									disabled={isPaid || weeklyBreakdown}
								/>
							</div>

							{jurisdiction == "UK" && ["6022"].includes(String(type)) && (
								<div className="col-xs-6 pr-0">
									<label>Notified On</label>
									<DatePicker
										selected={notificationDate || fromDate}
										onChange={(newDate) => {
											if (newDate) setNotificationDate(newDate);
										}}
										wrapperClassName="full-width"
										maxDate={addDays(toDate, 30)}
										disabled={isPaid || weeklyBreakdown}
										dateFormat="dd/MM/yyyy"
										customInput={<DateInputFormat />}
									/>
								</div>
							)}
							{date === dateTo && <BenefitType />}
						</div>
						<div className="col-xs-12 pl-0 pr-0 form-container">
							<LeaveBreakdown type={weeklyBreakdown ? "week" : "day"} />
						</div>
					</div>

					<div style={{ gridColumn: "3/4", borderLeft: "1px gray solid", paddingLeft: "1rem" }}>
						{isPaid ? (
							<div className="row col-xs-12">
								<Tooltip
									as="div"
									className="alert alert-warning"
									style={{ borderRadius: "1rem" }}
									content={
										<div style={{ margin: "1rem" }}>
											<p
												style={{
													textAlign: "left",
													color: "white",
													fontSize: "1.25rem",
												}}
											>
												To edit this leave entry, you must edit the linked payslip from the
												corresponding payroll.
											</p>
											<h6 style={{ textAlign: "left" }}>You may only edit:</h6>
											<ul
												style={{
													listStyle: "disc",
													float: "left",
													paddingLeft: "2.5rem",
													textAlign: "left",
												}}
											>
												<li>Leave days not yet paid in payslip</li>
												<li>Comments</li>
												<li>Attachments</li>
											</ul>
											<br />
											<a
												style={{
													fontSize: 11,
													padding: "3px 6px",
													paddingLeft: 0,
													textDecoration: "underline",
													float: "left",
													color: "white",
													cursor: "pointer",
												}}
												target="_blank"
												href="https://support.buddy.hr/hc/en-mt/articles/7078759037970-Editing-an-Entry-that-is-Marked-as-Paid"
											>
												Editing an Entry that is Marked As Paid
												<ArrowTopRightOnSquareIcon className="h-5 w-5" />
											</a>
										</div>
									}
								>
									<h5 style={{ color: "#232a32" }}>
										<ExclamationTriangleIcon
											className="h-7 w-7"
											style={{ verticalAlign: "middle", marginRight: "5px" }}
										/>
										{selectedEmployee?.label}'s leave has already been paid
									</h5>
								</Tooltip>
							</div>
						) : null}
						<div className="row">
							{newEntry || status == "Pending" ? (
								<div
									className="col-xs-12"
									style={{ paddingBottom: "2rem" }}
								>
									<label>Team Members on Leave</label>
									<ul>
										{!clashingLeaveExcludingThis.length && <li>None</li>}
										{clashingLeaveExcludingThis.length >= 5 ? (
											<li>{clashingLeaveExcludingThis?.length} Team Members</li>
										) : (
											<>
												{clashingLeaveExcludingThis?.map((leave) => (
													<li>{leave.employeeName}</li>
												))}
											</>
										)}
									</ul>
								</div>
							) : null}

							<div
								className="col-xs-12"
								style={{ paddingBottom: "2rem" }}
							>
								<label>Attachments</label>
								<ul style={{ listStyle: "none" }}>
									{uploading?.length > 0
										? uploading.map((fileName) => (
												<li>
													<Spinner
														style={{
															height: "1.5em",
															verticalAlign: "middle",
															float: "left",
														}}
														className="animate-spin -ml-1 mr-3 h-5 w-5 "
													/>
													<div style={{ float: "left" }}>{fileName}</div>
												</li>
										  ))
										: null}
									{uploaded?.length
										? uploaded.map((file) => (
												<li>
													<PhotoIcon
														style={{
															height: "1.5em",
															float: "left",
															marginRight: 5,
														}}
													/>
													<div style={{ float: "left" }}>{file.name}</div>
												</li>
										  ))
										: null}
								</ul>
								<label
									htmlFor="fileUpload"
									className="custom-file-upload btn btn-success"
									disabled={weeklyBreakdown}
								>
									<PhotoIcon
										style={{
											height: "1.5em",
											verticalAlign: "inherit",
											marginRight: 5,
										}}
									/>
									Upload Attachment
								</label>
								<input
									style={{ display: "none" }}
									id="fileUpload"
									type="file"
									accept=".pdf,.png,.jpg,.jpeg"
									disabled={weeklyBreakdown}
									onChange={(e) => {
										const file = e.target.files[0]; // accesing file
										uploadFile(file, selectedEmployee.value);
									}}
								/>
							</div>
						</div>
					</div>

					<div
						style={{
							gridColumn: "1/3",
						}}
					>
						<div className="">
							{!isNaN(hoursAvailable) && (
								<div className="col-xs-4 pl-0">
									<label>Balance</label>
									<div style={{ fontSize: "11px" }}>
										<div
											style={{
												border: `rgb(204,204,204) solid 1px`,
												borderRadius: "5px",
												padding: 5,
											}}
										>
											<strong>{(parseFloat(hoursAvailable) || 0).toFixed(2)}</strong> hours
											balance -
											<br />
											{type == 6022 && jurisdiction == "MT" && (
												<div style={{ fontSize: "11px" }}>
													<span>
														<strong>{halfPayBalance?.toFixed(2) || 0.0}</strong> hours of
														half pay
													</span>
												</div>
											)}
											{hoursPending ? (
												<>
													<strong>{parseFloat(hoursPending).toFixed(2)}</strong> hours pending
													-<br />
												</>
											) : null}
											<strong>{(parseFloat(hours) || 0).toFixed(2)}</strong> hours requested
											<br />
											<div
												style={{
													borderBottom: `rgb(204,204,204) solid 1px`,
												}}
											></div>
											<strong>
												{(
													hoursAvailable +
													(halfPayBalance || 0) -
													(hoursPending || 0) -
													hours
												)?.toFixed?.(2)}
											</strong>{" "}
											hours left after this leave
										</div>
									</div>
								</div>
							)}

							<div className="col-xs-4 pl-0">
								<label>Hours Requested</label>
								<input
									className="form-control form-input"
									name="hours"
									disabled={!isSameDay(fromDate, toDate) || isPaid || weeklyBreakdown}
									onChange={(e) => {
										const input = validateFloat(e.target.value);
										setHoursWorked(input);
									}}
									value={hours}
								/>
								<div className="clearfix" />
							</div>

							<div className="col-xs-4 pl-0">
								<label htmlFor="material-text">Status</label>
								<Select
									value={selectedStatus}
									options={leaveStatusOptions}
									onChange={(selected) => setLeaveStatus(selected.value)}
									isDisabled={isPaid || weeklyBreakdown}
								/>
							</div>
						</div>
					</div>

					<div style={{ gridColumn: "3/4", borderLeft: "1px gray solid", paddingLeft: "1rem" }}>
						<div className="col-xs-12">
							<label>Comment</label>
							<textarea
								className="form-control form-input"
								type="text"
								name="comment"
								style={{ minHeight: 100, maxHeight: 400, resize: "vertical" }}
								onChange={(e) => {
									setComment(e.target.value);
								}}
								disabled={weeklyBreakdown}
							>
								{comment}
							</textarea>
						</div>
					</div>

					<div
						className="block buttons"
						style={{ gridColumn: "1/4" }}
					>
						<SubmitButtons />
						<div className="clearfix" />
					</div>
				</div>
			</Modal>
			<Modal
				status={!!warningContent}
				close={() => setWarningContent(false)}
				title={warningContent.title}
				width="35vw"
			>
				<div className="block-content">
					<p>{warningContent.message}</p>

					<div className="block buttons">
						<button
							className="btn btn-danger pull-left"
							onClick={() => {
								setWarningContent(false);
							}}
						>
							Cancel
						</button>
						<button
							className="btn btn-success pull-right"
							onClick={() => saveLeaveData()}
							style={{ marginRight: 0 }}
						>
							Confirm
						</button>

						<div className="clearfix" />
					</div>
				</div>
			</Modal>
		</>
	);
};

const mapStateToProps = (state) => {
	const { employeeListWithSalary } = state.employees;

	const fullList = _.orderBy(
		employeeListWithSalary?.map((e) => ({
			value: e.id,
			label: toTitleCase(`${e.employeeName?.trim()} ${e.employeeSurname?.trim()}`),
			terminated: e.isTerminated,
			employmentType: e.employmentTypeName,
			description: [e.departmentName, e.roleName].filter(Boolean).join(" - "),
			statusMessage: e.terminationDate
				? "Last Working Day: " + format(new Date(e.terminationDate), "dd/MM/yyyy")
				: null,
		})) || [],
		["label", "terminated"],
		["asc", "asc"],
	);

	const terminated = fullList.filter((e) => e.terminated);

	const employeeOptions = employmentTypeOptions?.map((employmentType) => ({
		label: employmentType.label,
		options: fullList.filter((e) => !e.terminated && e.employmentType == employmentType.label),
	}));

	employeeOptions.push({
		label: "Terminated",
		options: terminated,
	});

	const [leaveTypeSettings, leaveTypes] = [state.leave?.leaveSettings?.leaveTypes, state.leave?.leaveTypes];
	const leaveTypeOptions = leaveTypes
		?.map((item) => ({ ...item, ...leaveTypeSettings?.find((setting) => setting?.id == item?.id) }))
		// ?.filter(item => item.parental)
		?.map((leaveType) => ({
			value: leaveType.id,
			label: leaveType.name,
			// parental: leaveType.parental,
			applyIfBalanceAvailable: !!leaveType.applyIfBalanceAvailable,
		}));

	const leaveStatusOptions = [
		{
			value: "Approved",
			label: "Approved",
		},
		{
			value: "Pending",
			label: "Pending",
		},
		{
			value: "Cancelled",
			label: "Cancelled",
		},
		{
			value: "Denied",
			label: "Denied",
		},
		{
			value: "Deleted",
			label: "Deleted",
		},
		{
			value: "Pending Cancellation",
			label: "Pending Cancellation",
		},
	];

	const clashingLeave = state.leave?.clashingLeave
		?.filter((leave) => !["Cancelled", "Denied", "Deleted"].includes(leave.status))
		?.map((leave) => ({
			...leave,
			employeeName: fullList?.find((employee) => employee.value == leave.agreement)?.label,
		}));

	const lockDate = false;

	const employee = {
		value: state.employee?.employeeDetails?.id,
		employmentType: state.employee?.salaries?.slice(-1)[0]?.employmentTypeName,
		label: `${state.employee?.employeeDetails?.employeeDetails?.givenName} ${state.employee?.employeeDetails?.employeeDetails?.familyName}`,
	};

	return {
		jurisdiction: state.company?.settings?.jurisdiction,
		modalLeave: state.leave.modalLeave || {},
		modalState: !!state.leave.modalState,
		modalSaving: state.leave.modalSaving,
		employeeOptions: employeeOptions.flatMap(({ options }) => options),
		displayEmployeeOptions: employeeOptions,
		lockDate,
		leaveTypeOptions,
		leaveStatusOptions,
		clashingLeave,
		updatingClashingLeave: state.leave.clashingLeaveUpdating,
		uploading: state.leave.filesUploading,
		uploaded: state.leave.filesUploaded,
		maxDailyLeaveHours: state?.leave?.leaveSettings?.maxDailyLeaveHours,
		currentAgreement: state?.leave?.modalStatus?.employee || {},
		modalStatus: state.leave?.modalStatus?.status || {},
		leaveBalances: state.leave?.leaveBalancesByType,
		modalDetailsLoading: state.leave?.modalDetailsLoading,
		// canEdit: (state.permissions && state.permissions.my && state.permissions.my.leave) != "Read",
		employee,
		location: state.leave?.modalLeave?.location,
	};
};

const mapDispatchToProps = (dispatch) => ({
	updateField: (field, newValue) => dispatch(ACTIONS.modalUpdateField(field, newValue)),
	setOpenModal: (status, timesheet) => dispatch(ACTIONS.setOpenModal(status, timesheet)),
	saveLeave: (leave) => dispatch(ACTIONS.saveLeave(leave)),
	getLeave: (options) => dispatch(ACTIONS.leaveGet(options, true)),
	getLeaveBreakdown: (id) => dispatch(ACTIONS.leaveGetBreakdown(id)),
	uploadFile: (file, agreement) => dispatch(ACTIONS.uploadFile({ file, agreement, uploadType: "LEAVE_MODAL" })),
	employeesGetList: () => dispatch(EMPLOYEEACTIONS.employeesGetList()),
	updateModalStatus: () => dispatch(ACTIONS.updateModalStatus()),
	mapBreakdown: () => dispatch(ACTIONS.mapBreakdown()),
	setSickType: () => dispatch(ACTIONS.setSickType()),
	modalUpdateDates: (field, value) => dispatch(ACTIONS.modalUpdateDates(field, value)),
	modalUpdateDetails: (field, value) => dispatch(ACTIONS.modalUpdateDetails(field, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LeaveModal);
